import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone,
  Building2,
  User,
  Clock,
  ArrowUpDown,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Plus
} from 'lucide-react';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Input } from '@/components/common/Input';
import { useToast } from '@/components/common/Toast';
import CallViewHeader from './CallViewHeader';
import CallStatsCards from './CallStatsCards';

import { CALL_TYPE_CONFIG, STATUS_CONFIG } from '@/theme/corporateTheme';

interface CallEvent {
  id: string;
  contactId: string;
  callType: 'CALL1' | 'CALL2' | 'CALL3';
  dueDate: string;
  status: 'OPEN' | 'DONE' | 'SKIPPED';
  contact?: {
    firstName: string;
    lastName: string;
    account?: {
      name: string;
    };
  };
  assignedTo?: {
    name: string;
  };
  callPackData?: {
    notes?: string;
    duration?: number;
  };
}

export default function CallTableView() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [calls, setCalls] = useState<CallEvent[]>([]);
  const [filteredCalls, setFilteredCalls] = useState<CallEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'OPEN' | 'DONE'>('all');
  const [filterType, setFilterType] = useState<'all' | 'CALL1' | 'CALL2' | 'CALL3'>('all');
  const [sortField, setSortField] = useState<'dueDate' | 'contact' | 'account' | 'type'>('dueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadCalls();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [calls, searchTerm, filterStatus, filterType, sortField, sortOrder]);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setCalls(result.data || []);
      }
    } catch (error) {
      console.error('Error loading calls:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to load calls',
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...calls];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(call =>
        `${call.contact?.firstName} ${call.contact?.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        call.contact?.account?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(call => call.status === filterStatus);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(call => call.callType === filterType);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'dueDate':
          aVal = new Date(a.dueDate).getTime();
          bVal = new Date(b.dueDate).getTime();
          break;
        case 'contact':
          aVal = `${a.contact?.firstName} ${a.contact?.lastName}`.toLowerCase();
          bVal = `${b.contact?.firstName} ${b.contact?.lastName}`.toLowerCase();
          break;
        case 'account':
          aVal = (a.contact?.account?.name || '').toLowerCase();
          bVal = (b.contact?.account?.name || '').toLowerCase();
          break;
        case 'type':
          aVal = a.callType;
          bVal = b.callType;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredCalls(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (status === 'DONE') return false;
    return new Date(dueDate) < new Date();
  };

  // Pagination
  const totalPages = Math.ceil(filteredCalls.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCalls = filteredCalls.slice(startIndex, endIndex);

  // Calculate stats
  const stats = {
    total: calls.length,
    scheduled: calls.filter(c => c.status === 'OPEN').length,
    completed: calls.filter(c => c.status === 'DONE').length,
    overdue: calls.filter(c => isOverdue(c.dueDate, c.status)).length,
    dueToday: calls.filter(c => {
      const due = new Date(c.dueDate);
      const today = new Date();
      return due.toDateString() === today.toDateString();
    }).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Phone className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-500">Loading calls...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unified Header */}
      <CallViewHeader
        currentView="table"
        title="Call Management"
        subtitle="Professional data table with sorting and filtering"
        showCreateButton={false}
      />

      {/* Unified Stats */}
      <CallStatsCards stats={stats} variant="compact" />

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search contacts or accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="OPEN">Scheduled</option>
            <option value="DONE">Completed</option>
          </select>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Types</option>
            <option value="CALL1">Alignment Call #1</option>
            <option value="CALL2">Confirmation Call #2</option>
            <option value="CALL3">Follow-up Call</option>
          </select>

          {/* Results Count */}
          <div className="flex items-center text-sm text-gray-600">
            <Filter className="w-4 h-4 mr-2" />
            {filteredCalls.length} of {calls.length} calls
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSort('contact')}
                >
                  <div className="flex items-center gap-2">
                    Contact
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSort('account')}
                >
                  <div className="flex items-center gap-2">
                    Account
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSort('type')}
                >
                  <div className="flex items-center gap-2">
                    Type
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSort('dueDate')}
                >
                  <div className="flex items-center gap-2">
                    Due Date
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCalls.map(call => (
                <tr 
                  key={call.id}
                  onClick={() => navigate(`/calls/${call.id}`)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {call.contact?.firstName} {call.contact?.lastName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                      {call.contact?.account?.name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge className={CALL_TYPE_CONFIG[call.callType].color}>
                      {CALL_TYPE_CONFIG[call.callType].label}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm">
                      <Clock className="w-4 h-4 mr-2 text-gray-400" />
                      <span className={isOverdue(call.dueDate, call.status) ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                        {formatDate(call.dueDate)}
                      </span>
                      {isOverdue(call.dueDate, call.status) && (
                        <Badge className="ml-2 bg-red-100 text-red-700">Overdue</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge className={STATUS_CONFIG[call.status].color}>
                      {STATUS_CONFIG[call.status].label}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {call.assignedTo?.name || 'Unassigned'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {call.callPackData?.duration || 60} min
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredCalls.length)} of {filteredCalls.length} results
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Button
                  key={page}
                  variant={currentPage === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {paginatedCalls.length === 0 && (
          <div className="text-center py-12">
            <Phone className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No calls found matching your filters</p>
          </div>
        )}
      </Card>
    </div>
  );
}
